import React, { createContext, useContext, useState, useEffect } from 'react';
import apiService from '../services/api';
import { usePermissionContext } from './PermissionContext';

const SubscriptionContext = createContext();

export const useSubscriptionContext = () => {
  const context = useContext(SubscriptionContext);
  if (!context) {
    throw new Error('useSubscriptionContext must be used within a SubscriptionProvider');
  }
  return context;
};

export const SubscriptionProvider = ({ children }) => {
  const [activeSubscription, setActiveSubscription] = useState(null);
  const [isLoading, setIsLoading] = useState(true);

  // Initial load on mount
  useEffect(() => {
    const token = localStorage.getItem('etpos_token');
    if (token) {
      loadSubscription();
    } else {
      setIsLoading(false);
    }

    // Listen for login events to reload subscription for new users
    const handleLoginEvent = () => {
      // console.log('🔄 Subscription DEBUG - Login event detected, clearing and reloading subscription');
      setActiveSubscription(null);
      loadSubscription();
    };

    const handleReloadSub = () => {
      // console.log('🔄 Subscription DEBUG - Event reload-subscription received, reloading subscription');
      loadSubscription();
    };

    window.addEventListener('user-logged-in', handleLoginEvent);
    window.addEventListener('reload-subscription', handleReloadSub);

    return () => {
      window.removeEventListener('user-logged-in', handleLoginEvent);
      window.removeEventListener('reload-subscription', handleReloadSub);
    };
  }, []);

  const loadSubscription = async () => {
    // console.log('🔄 Subscription DEBUG - Loading active subscription...');
    try {
      setIsLoading(true);
      const subRes = await apiService.getActiveSubscription();
      // console.log('🔄 Subscription DEBUG - Active subscription received:', subRes);
      
      const subData = subRes.data || subRes;
      
      // The backend returns an object that either has subscription details 
      // OR just 'package_details' if they are on a dummy/local instance.
      if (subData && Object.keys(subData).length > 0) {
        setActiveSubscription(subData);
      } else {
        setActiveSubscription(null);
      }
    } catch (error) {
      // console.error('🔄 Subscription DEBUG - Error loading subscription:', error);
      setActiveSubscription(null);
    } finally {
      setIsLoading(false);
    }
  };

  const { isSuperAdmin } = usePermissionContext();
  const isSuperAdminUser = isSuperAdmin;

  /**
   * Helper function to check if a specific module is enabled in the active subscription
   * @param {string} moduleName - e.g. 'crm', 'woocommerce', 'repair'
   * @returns {boolean}
   */
  const hasModule = (moduleName) => {
    if (isSuperAdminUser) return true;
    // If no active subscription, maybe default to false or fallback to true based on backend logic
    if (!activeSubscription) return false;
    
    // Normalize module names to match backend database columns (e.g. aiassistance_module)
    let cleanModuleName = moduleName || '';
    if (cleanModuleName === 'ai_assistance' || cleanModuleName === 'ai-assistant') {
      cleanModuleName = 'aiassistance';
    }
    
    // Check package_details (superadmin checks this for quotas and modules)
    const packageDetails = activeSubscription.package_details || {};
    const moduleKey = `${cleanModuleName}_module`;
    
    // Superadmin logic typically passes "1" or true if enabled
    return packageDetails[moduleKey] === 1 || packageDetails[moduleKey] === '1' || packageDetails[moduleKey] === true;
  };

  /**
   * Helper function to check if a quota has been reached.
   * e.g., 'user_count', 'product_count', 'location_count', 'invoice_count'
   * Returns true if quota is available, false if limit reached.
   */
  const isQuotaAvailable = (quotaKey) => {
    if (isSuperAdminUser) return true;
    if (!activeSubscription) return true;
    
    const packageDetails = activeSubscription.package_details || {};
    const allowed = packageDetails[quotaKey];
    
    // 0, null, or undefined means unlimited in backend logic
    if (allowed === undefined || allowed === null || allowed === '' || allowed === 0 || allowed === '0') return true;
    
    const allowedNum = Number(allowed);
    if (isNaN(allowedNum) || allowedNum <= 0) return true;
    
    const usedKey = `${quotaKey.replace('_count', '')}s_created`; // user_count -> users_created
    const used = Number(activeSubscription[usedKey] || 0);
    
    return used < allowedNum;
  };

  return (
    <SubscriptionContext.Provider value={{
      activeSubscription,
      isLoading,
      hasModule,
      isQuotaAvailable,
      reloadSubscription: loadSubscription
    }}>
      {children}
    </SubscriptionContext.Provider>
  );
};
